﻿module Illy.SafeStack.Client.Select

open Elmish
open Illy.SafeStack
open Fable.React
open Fable.React.Props
open Fable.Core.JsInterop

type Model<'a> = 
    {
        Id: string
        Label: string
        ValidationError: string option
        ValidationWarning: string option
        Placeholder: string option
        Options: ListType<'a> option
        SelectedOption: ListOptionType<'a> option

        //Snapshot of the selected option on init
        SelectedSnapshot: ListOptionType<'a> option
        Mandatory: bool
    }

type Msg<'a> = 
    | UpdateListOptions of ListType<'a>
    | SelectOptionChanged of ListOptionType<'a> option


let private initInternal id label placeholder mandatory options selected = 
    {
        Id = id
        Label = label
        ValidationError = None
        ValidationWarning = None
        Placeholder = placeholder
        Options = options
        SelectedOption = selected
        SelectedSnapshot = selected
        Mandatory = mandatory
    }

let init id label placeholder mandatory = 
    initInternal id label placeholder mandatory None None

let initWithOptions id label placeholder mandatory options selected = 
    initInternal id label placeholder mandatory (Some options) selected

let update<'a> (msg: Msg<'a>) (model: Model<'a>) : Model<'a>*Cmd<Msg<'a>> = 
    match msg with
    | UpdateListOptions list-> 
        {model with Options = Some list}, []
    | SelectOptionChanged opt ->
        {model with SelectedOption = opt}, []

///Add item to the list if it does not exist already
let private addToList list =
    function
    | None -> list
    | Some item ->  
        if List.exists (fun x -> x = item) list then 
            list
        else 
            item::list 

let selectInput<'a> loading (model: Model<'a>) (dispatch: Msg<'a> -> unit) =
    let trySendChange str =
        if str = "" then
            dispatch (SelectOptionChanged None)
        else
            let id = System.Int32.Parse str
            match model.Options with
            | Some opts ->
                let found = ListOption.getListItemById id opts
                dispatch (SelectOptionChanged <| Some found)
            | None -> ()
    
    let selectedOptIdValue = 
        model.SelectedOption 
        |> Option.map (ListOptionType.getListOption >> ListOption.getId >> (fun x -> x.ToString()))
        |> Option.defaultValue ""
        
    select [ 
            Id model.Id 
            ClassName (jQueryUI.addSkeletonScreen "form-control" None loading)
            OnChange (fun ev -> trySendChange !!ev.target?value)
            Value selectedOptIdValue
        ] [
            match model.Options with 
            | Some options -> 
                match model.Placeholder with
                | None -> ()
                | Some pl -> yield option [ Value "" ] [ str pl ]

                let optionsWithHiddenItem = 
                    options
                    |> ListOption.convertToList
                    |> addToList <| (FSharp.Core.Option.map ListOptionType.getListOption model.SelectedSnapshot)

                let groups = optionsWithHiddenItem |> List.groupBy (fun x -> x.Group)
                let (ungrouped, grouped) = groups |> List.partition (fun x -> fst x = "")
                for (g, opts) in grouped do
                    yield optgroup [
                        Label g
                    ] [
                        for opt in opts do
                            yield option [ Value (opt.Id.ToString()) ] [ str opt.Value ] 
                    ]
                if ungrouped.Length > 0 then
                    let options = [
                        for opt in snd(ungrouped.[0]) do
                            yield option [ Value (opt.Id.ToString()) ] [ str opt.Value ]
                    ]
                    if grouped.Length = 0 then
                        for opt in options do yield opt
                    else
                        yield optgroup [ Label "Ungrouped" ] options
            | None -> ()
        ]

let select loading model dispatch =    
    jQueryUI.inputGroup [
        jQueryUI.labelWithMandatory model.Id model.Label model.ValidationError model.ValidationWarning loading model.Mandatory
        selectInput loading model dispatch  
    ]

//utility functions
let resetValidations (model: Model<_>) = 
    {model with ValidationError = None; ValidationWarning = None}

let setValidations (model: Model<_>) errors warnings = 
    {model with ValidationError = errors; ValidationWarning = warnings }

let toListOption (model: Model<_>) =
    FSharp.Core.Option.map ListOptionType.getListOption model.SelectedOption

let resetSelectedOption (model: Model<_>) = {model with SelectedOption = None}